import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="bg-[#00124a] py-6">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white hover:text-[#3c68ff] transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#00124a] mb-8">Privacy Policy</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-[#4A5568] mb-6 text-base leading-relaxed">
            <strong>Effective Date:</strong> January 1, 2025
          </p>

          <p className="text-[#4A5568] mb-6 text-base leading-relaxed">
            Amplify HR Management, LLC ("Amplify," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
          </p>

          <h2 className="text-xl font-bold text-[#00124a] mt-8 mb-4">1. Information We Collect</h2>
          <p className="text-[#4A5568] mb-4 text-base leading-relaxed">
            We may collect information about you in a variety of ways, including:
          </p>
          <ul className="list-disc pl-6 mb-6 text-[#4A5568] space-y-2">
            <li><strong>Personal Data:</strong> Name, email address, phone number, company name, and other contact information you voluntarily provide.</li>
            <li><strong>Business Information:</strong> Company size, current PEO/payroll provider, and other business-related details.</li>
            <li><strong>Usage Data:</strong> Information about how you access and use our website, including IP address, browser type, pages visited, and time spent on pages.</li>
            <li><strong>Cookies and Tracking:</strong> We use cookies and similar tracking technologies to track activity on our website and improve user experience.</li>
          </ul>

          <h2 className="text-xl font-bold text-[#00124a] mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="text-[#4A5568] mb-4 text-base leading-relaxed">
            We may use the information we collect for various purposes, including to:
          </p>
          <ul className="list-disc pl-6 mb-6 text-[#4A5568] space-y-2">
            <li>Respond to your inquiries and provide customer service</li>
            <li>Send you marketing and promotional communications (with your consent)</li>
            <li>Improve our website and services</li>
            <li>Comply with legal obligations</li>
            <li>Protect against fraudulent or unauthorized activity</li>
          </ul>

          <h2 className="text-xl font-bold text-[#00124a] mt-8 mb-4">3. Disclosure of Your Information</h2>
          <p className="text-[#4A5568] mb-4 text-base leading-relaxed">
            We may share your information in the following situations:
          </p>
          <ul className="list-disc pl-6 mb-6 text-[#4A5568] space-y-2">
            <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf</li>
            <li><strong>Legal Requirements:</strong> If required by law or in response to valid legal process</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Your Consent:</strong> For any other purpose with your consent</li>
          </ul>

          <h2 className="text-xl font-bold text-[#00124a] mt-8 mb-4">4. Data Security</h2>
          <p className="text-[#4A5568] mb-6 text-base leading-relaxed">
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
          </p>

          <h2 className="text-xl font-bold text-[#00124a] mt-8 mb-4">5. Your Rights</h2>
          <p className="text-[#4A5568] mb-4 text-base leading-relaxed">
            Depending on your location, you may have certain rights regarding your personal information, including:
          </p>
          <ul className="list-disc pl-6 mb-6 text-[#4A5568] space-y-2">
            <li>The right to access your personal data</li>
            <li>The right to correct inaccurate data</li>
            <li>The right to request deletion of your data</li>
            <li>The right to opt out of marketing communications</li>
          </ul>

          <h2 className="text-xl font-bold text-[#00124a] mt-8 mb-4">6. Third-Party Links</h2>
          <p className="text-[#4A5568] mb-6 text-base leading-relaxed">
            Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
          </p>

          <h2 className="text-xl font-bold text-[#00124a] mt-8 mb-4">7. Changes to This Policy</h2>
          <p className="text-[#4A5568] mb-6 text-base leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" above.
          </p>

          <h2 className="text-xl font-bold text-[#00124a] mt-8 mb-4">8. Contact Us</h2>
          <p className="text-[#4A5568] mb-6 text-base leading-relaxed">
            If you have questions about this Privacy Policy or our privacy practices, please contact us at:
          </p>
          <div className="bg-white rounded-lg p-6 border border-[#e2e8f0]">
            <p className="text-[#00124a] font-bold mb-2">Amplify HR Management, LLC</p>
            <p className="text-[#4A5568]">Email: <a href="mailto:success@amplifyhrm.com" className="text-[#3c68ff] hover:underline">success@amplifyhrm.com</a></p>
            <p className="text-[#4A5568]">Phone: <a href="tel:8558088689" className="text-[#3c68ff] hover:underline">(855) 808-8689</a></p>
            <p className="text-[#4A5568]">Website: <a href="https://amplifyhrm.com" className="text-[#3c68ff] hover:underline">amplifyhrm.com</a></p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#00124a] py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[#6B7280] text-sm">
            © {new Date().getFullYear()} Amplify HR Management, LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
